import { memo, useMemo } from "react";
import { normalizePitchClass } from "@/music/chords/chordParser";
import { getChordDetails } from "@/music/chords/chordTheoryEngine";

const WHITE_KEYS = ["C", "D", "E", "F", "G", "A", "B"];
const BLACK_KEYS = [
  { note: "C#", after: 0 },
  { note: "D#", after: 1 },
  { note: "F#", after: 3 },
  { note: "G#", after: 4 },
  { note: "A#", after: 5 },
];

const PianoChordMiniDiagram = memo(({ chordName, notes, octaveRange = 1, variant = "dark" }) => {
  const details = useMemo(() => getChordDetails(chordName), [chordName]);
  const activeNotes = useMemo(() => {
    const source = Array.isArray(notes) && notes.length ? notes : details.pitchClasses;
    return new Set(source.map(normalizePitchClass).filter(Boolean));
  }, [details.pitchClasses, notes]);
  const whiteKeys = Array.from({ length: Math.max(1, octaveRange) }).flatMap(() => WHITE_KEYS);
  const whiteWidth = 112 / whiteKeys.length;

  return (
    <figure className="w-[9rem] shrink-0 text-center" aria-label={`Mini piano ${details.displayName || "sin acorde"}`}>
      <svg viewBox="0 0 116 54" className="h-[4.25rem] w-full" role="img">
        <rect x="2" y="2" width="112" height="48" rx="4" fill="#09090b" stroke="#334155" />
        {whiteKeys.map((note, index) => {
          const active = activeNotes.has(note);
          const x = 2 + index * whiteWidth;
          return (
            <g key={`${note}-${index}`}>
              <rect x={x} y="3" width={whiteWidth} height="46" fill={active ? "#a5f3fc" : "#f8fafc"} stroke="#64748b" strokeWidth="0.6" />
              {active && <circle cx={x + whiteWidth / 2} cy="38" r="3.2" fill="#0891b2" />}
            </g>
          );
        })}
        {BLACK_KEYS.map((key) => {
          const x = 2 + (key.after + 0.72) * whiteWidth;
          const active = activeNotes.has(key.note);
          return (
            <g key={key.note}>
              <rect x={x} y="3" width={whiteWidth * 0.58} height="27" rx="1" fill={active ? "#67e8f9" : "#111827"} />
              {active && <circle cx={x + whiteWidth * 0.29} cy="20" r="2.6" fill="#0e7490" />}
            </g>
          );
        })}
      </svg>
      <figcaption className={`mt-1 truncate text-sm font-semibold ${variant === "light" ? "text-slate-800" : "text-cyan-100"}`}>
        {details.displayName || "--"}
      </figcaption>
    </figure>
  );
});

PianoChordMiniDiagram.displayName = "PianoChordMiniDiagram";

export default PianoChordMiniDiagram;
